// Cola local de conteos de la toma física.
//
// POR QUÉ EXISTE: la toma física se hace caminando la bodega, y en una bodega
// no siempre hay señal. Sin cola, cada conteo sin red se pierde y el operario
// no se entera hasta volver a la oficina — que es exactamente cuando ya no
// puede volver a mirar el activo.
//
// Es DELIBERADAMENTE pequeña: localStorage y nada más. No hace falta IndexedDB
// ni un service worker como en el POS, porque aquí sólo se guardan placas: unos
// cientos de cadenas cortas que se drenan en cuanto vuelve la red.
import { afRpc } from './shared';

export interface ConteoPendiente {
  placa: string;
  ubicacion_hallada_id: string | null;
  ts: number;
}

const clave = (tomaId: string) => `af_toma_cola_${tomaId}`;

function leer(tomaId: string): ConteoPendiente[] {
  try {
    const s = localStorage.getItem(clave(tomaId));
    return s ? (JSON.parse(s) as ConteoPendiente[]) : [];
  } catch { return []; }   // navegador con almacenamiento bloqueado: se sigue sin cola
}

function escribir(tomaId: string, cola: ConteoPendiente[]) {
  try { localStorage.setItem(clave(tomaId), JSON.stringify(cola)); } catch { /* sin cola */ }
}

export function encolar(tomaId: string, c: ConteoPendiente): number {
  const cola = leer(tomaId);
  // La misma placa dos veces en la cola no aporta nada: el conteo es idempotente.
  const i = cola.findIndex((x) => x.placa.toUpperCase() === c.placa.toUpperCase());
  if (i >= 0) cola[i] = c; else cola.push(c);
  escribir(tomaId, cola);
  return cola.length;
}

export function pendientes(tomaId: string): ConteoPendiente[] { return leer(tomaId); }

/** Intenta enviar la cola. Devuelve cuántos entraron y cuántos quedaron.
 *  Un conteo que el servidor RECHAZA (placa inexistente, toma cerrada) se saca
 *  de la cola y se reporta: dejarlo dentro la atascaría para siempre. */
export async function drenar(tomaId: string): Promise<{ enviados: number; rechazados: string[]; quedan: number }> {
  const cola = leer(tomaId);
  if (!cola.length) return { enviados: 0, rechazados: [], quedan: 0 };

  let enviados = 0;
  const rechazados: string[] = [];
  const restantes: ConteoPendiente[] = [];

  for (const c of cola) {
    try {
      await afRpc('af_toma_contar', {
        _toma_id: tomaId, _activo_id: null, _placa: c.placa,
        _ubicacion_hallada_id: c.ubicacion_hallada_id, _custodio_hallado_id: null, _observacion: null,
      });
      enviados++;
    } catch (e) {
      // Sin red se reintenta después; un rechazo del servidor no se reintenta
      // nunca porque volvería a fallar igual.
      const err = e as { code?: string; response?: { status?: number } };
      const sinRed = err?.code === 'ERR_NETWORK' || !err?.response;
      if (sinRed) restantes.push(c); else rechazados.push(c.placa);
    }
  }

  escribir(tomaId, restantes);
  return { enviados, rechazados, quedan: restantes.length };
}

export function limpiar(tomaId: string) {
  try { localStorage.removeItem(clave(tomaId)); } catch { /* nada */ }
}
